import { Chart } from 'react-chartjs-2';
import Alert from 'reactstrap/lib/Alert';
import Col from 'reactstrap/lib/Col';
import Row from 'reactstrap/lib/Row';
import { BarController, BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, LineController, LineElement, PointElement, Tooltip } from 'chart.js';
import moment from 'moment';

import Card from 'Lib/components/common/Card';
import Currency from 'Lib/components/common/Currency';
import Date from 'Lib/components/common/Date';
import { Table } from 'Lib/components/common/table';
import Text from 'Lib/components/common/Text';
import IncomeReportStatuses from 'Lib/constants/screening/incomeReportStatuses';
import IncomeTransactionTypes from 'Lib/constants/screening/incomeTransactionTypes';

import DocumentImages from './components/DocumentImages';

ChartJS.register(LinearScale, CategoryScale, BarElement, PointElement, LineElement, Legend, Tooltip, LineController, BarController);

const IncomeSummary = ({ estimate }) => {
	if (!estimate) return null;

	const { estimatedIncome, statedIncome, difference, numMonthsForSample } = estimate;

	const renderDifference = difference => {
		if (!difference) {
			return null;
		}

		let icon;
		let value;
		if (difference >= 0) {
			icon = <i className="fas fa-arrow-up" style={{ color: '#44b449' }}></i>;
			value = difference;
		} else {
			icon = <i className="fas fa-arrow-down" style={{ color: '#e06161' }}></i>;
			value = difference * -1;
		}

		return (
			<span>
				({icon} {value}%)
			</span>
		);
	};

	return (
		<>
			<dt>Stated Monthly Income</dt>
			<dd>{statedIncome ? <Currency value={statedIncome} /> : <Text value="No income provided" />}</dd>
			<dt>Estimated Monthly Income</dt>
			<dd>
				{estimatedIncome ? (
					<>
						<Currency value={estimatedIncome} /> {renderDifference(difference)}
						<p style={{ color: '#959595', fontSize: '.8rem' }}>over last {numMonthsForSample} months, only includes payroll transactions</p>
					</>
				) : (
					<Text value="Could not estimate income" />
				)}
			</dd>
		</>
	);
};

const MonthlyIncomeChart = ({ byMonth, movingAverage }) => {
	if (!byMonth) return null;

	const formatMonth = value => moment(value, 'YYYY-MM').format('MMM YYYY');

	const averageForMonth = month => {
		const value = movingAverage.find(data => data.month == month);

		return value?.amount ? '$' + value.amount : 'N/A';
	};

	const formatAxisAmount = value => '$' + (value > 999 ? `${value / 1000}k` : value);

	// const labels = byMonth.map(value => formatMonth(value.month)).filter((_, index) => index % 6 === 0);
	const labels = byMonth.map(value => formatMonth(value.month));

	const options = {
		elements: {
			line: {
				cubicInterpolationMode: 'monotone' // 'default', 'monotone', 'linear', 'fast', or 'none'
			},
			point: {
				radius: 0 // Set the radius to 0 to hide the points
			}
		},
		plugins: {
			tooltip: {
				callbacks: {
					label: function (context) {
						// Customize the tooltip label based on the hovered data point
						const value = context.parsed.y || 0;

						return `Income: $${value} \n\b6 month avg: ${averageForMonth(byMonth[context.dataIndex].month)}`;
					}
				}
			}
		},
		scales: {
			x: {
				ticks: {
					maxTicksLimit: 5, // Set the maximum number of labels to display
					barPercentage: 1.0, // Adjust the width of the bars (0.0 to 1.0)
					categoryPercentage: 1.0 // Adjust the space between bars (0.0 to 1.0)
				}
			},
			y: {
				beginAtZero: true,
				ticks: {
					callback: function (value) {
						return formatAxisAmount(value);
					}
				}
			}
		}
	};

	const data = {
		labels,
		datasets: [
			{
				type: 'line',
				label: 'Average Income',
				borderColor: 'rgb(255, 99, 132)',
				borderWidth: 2,
				fill: false,
				data: movingAverage.map(value => value.amount)
			},
			{
				type: 'bar',
				label: 'Month',
				backgroundColor: 'rgb(75, 192, 192)',
				data: byMonth.map(value => value.amount),
				borderColor: 'white',
				borderWidth: 2
			}
		]
	};

	return <Chart type="bar" data={data} options={options} />;
};

const ReportProcessing = () => (
	<div className="text-center">
		<i className="fas fa-spinner fa-spin" style={{ marginRight: '6px' }} />
		Report is still processing, please check again in a few minutes
	</div>
);

const AccountInfoList = ({ accountInfo, processing }) => {
	return accountInfo?.length ? (
		<div style={{ maxHeight: '300px', overflowY: 'scroll' }}>
			<Table striped isClickable={false}>
				<thead>
					<tr>
						<th>Account</th>
						<th>Type</th>
						<th>Owners</th>
					</tr>
				</thead>
				<tbody>
					{accountInfo.map((account, index) => {
						return (
							<tr key={index}>
								<td>
									<Text value={account.account} />
								</td>
								<td>
									<Text value={account.type} />
								</td>
								<td>
									<Text value={account.owners} />
								</td>
							</tr>
						);
					})}
				</tbody>
			</Table>
		</div>
	) : processing ? (
		<ReportProcessing />
	) : (
		<div className="text-center">No accounts for applicant</div>
	);
};

const TransactionList = ({ transactions, processing }) => {
	return transactions?.length ? (
		<div style={{ maxHeight: '300px', overflowY: 'scroll' }}>
			<Table striped isClickable={false}>
				<thead>
					<tr>
						<th>Date</th>
						<th>Type</th>
						<th>Account</th>
						<th className="text-right">Amount</th>
					</tr>
				</thead>
				<tbody>
					{transactions.map((transaction, index) => {
						return (
							<tr key={index}>
								<td>
									<Date value={transaction.date} />
								</td>
								<td>
									<Text value={IncomeTransactionTypes.getName(transaction.categoryID)} />
								</td>
								<td>
									<Text value={transaction.accountName} />
								</td>
								<td className="text-right">
									<Currency value={transaction.amount} />
								</td>
							</tr>
						);
					})}
				</tbody>
			</Table>
		</div>
	) : processing ? (
		<ReportProcessing />
	) : (
		<div className="text-center">No transactions for applicant</div>
	);
};

const renderStatus = (statusID, verificationActive, verificationErrors) => {
	const alertStyle = { marginLeft: '10px', marginRight: '10px' };

	switch (parseInt(statusID, 10)) {
		case IncomeReportStatuses.Failed:
			return (
				<Alert color="danger" style={alertStyle}>
					{verificationErrors?.length ? (
						<>
							The Income Report has failed with the following errors:
							<ul style={{ marginBottom: '0' }}>
								{verificationErrors.map((error, index) => (
									<li key={index}>{error}</li>
								))}
							</ul>
						</>
					) : (
						'The Income Report has failed, please contact Rentvine support.'
					)}
				</Alert>
			);
		case IncomeReportStatuses.Skipped:
			return (
				<Alert color="warning" style={alertStyle}>
					This applicant has opted-out or was unable to complete the Income Verification. Manually uploaded documents are shown below if required by the application
					template.
				</Alert>
			);
		case IncomeReportStatuses.NotEnabled:
			return (
				<Alert color="warning" style={alertStyle}>
					{verificationActive
						? 'Income Verification was not enabled when this application was submitted.'
						: 'Income Verification is not enabled for this application template. To enable, turn on Income Verification in the template builder.'}
				</Alert>
			);
		default:
			return null;
	}
};

const TransactionsPage = ({ incomeTransactions }) => {
	const { byMonth, movingAverage, estimate, transactions, statusID, verificationActive, verificationErrors, accountInfo, files } = incomeTransactions;

	const processing = statusID == IncomeReportStatuses.Processing;

	return (
		<>
			{renderStatus(statusID, verificationActive, verificationErrors)}
			<Row>
				<Col md="5">
					<Card title="Summary">
						<IncomeSummary estimate={estimate} />
					</Card>
					<Card title="Monthly Income">
						<MonthlyIncomeChart byMonth={byMonth} movingAverage={movingAverage} />
					</Card>
				</Col>
				<Col md="7">
					{files && files.length > 0 && (
						<Card title="Documents">
							<DocumentImages files={files} />
						</Card>
					)}
					<Card title="Accounts">
						<AccountInfoList accountInfo={accountInfo} processing={processing} />
					</Card>
					<Card title="Transactions">
						<TransactionList transactions={transactions} processing={processing} />
					</Card>
				</Col>
			</Row>
		</>
	);
};

export default TransactionsPage;
